import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import {
  GetScratchPlatesForNotePart,
  GetScratchPlatesForNotePart_scratchPlates as ScratchPlateQueryNode,
  GetScratchPlatesForNotePart_scratchPlates_ScratchPlateNode as ScratchPlateNode,
  GetScratchPlatesForNotePartVariables,
} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlatesForNotePart';
import {GET_SCRATCH_PLATES_FOR_NOTE_PART_QUERY} from 'notebook/integrations/plate-maps/scratch-plates/GetScratchPlatesForNotePart';
import {MiniPlateSummaryFilledPosition} from 'notebook/integrations/plate-maps/scratch-plates/MiniPlateSummary';
import ScratchPlateRecordsView, {
  RecordsListItem,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateRecordsView';
import {ScratchPlateReferenceData} from 'notebook/util/NoteUtilConstants';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {PlateSize} from 'samples/storables/StorableSchemaConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export function buildScratchPlateRecordsItems(
  scratchPlates: Array<ScratchPlateQueryNode>
): Array<RecordsListItem> {
  return scratchPlates.map((scratchPlate): RecordsListItem => {
    if (!isInaccessible(scratchPlate)) {
      return {
        kind: 'plate',
        id: scratchPlate.id,
        name: scratchPlate.name,
        createdAt: scratchPlate.createdAt,
        inventoryPlateId:
          scratchPlate.inventoryPlate == null
            ? null
            : isInaccessible(scratchPlate.inventoryPlate)
              ? scratchPlate.inventoryPlate.invalidId
              : scratchPlate.inventoryPlate.id,
        inventoryPlateInaccessible:
          scratchPlate.inventoryPlate != null && isInaccessible(scratchPlate.inventoryPlate),
        width: scratchPlate.width,
        height: scratchPlate.height,
        filledPositions:
          (scratchPlate.summary?.wellStates as Array<MiniPlateSummaryFilledPosition> | undefined) ?? null,
      };
    }
    const inaccessibleId = scratchPlate.invalidId;
    return {
      kind: 'inaccessible',
      id: `inaccessible-${inaccessibleId}`,
      invalidId: inaccessibleId,
    };
  });
}

export default function MultiScratchPlateNotePartView({
  notePartCid,
  scratchPlates,
}: {
  notePartCid: string;
  scratchPlates: Array<ScratchPlateReferenceData>;
}): JSX.Element {
  const query = useSkippableBenchlingQuery<GetScratchPlatesForNotePart, GetScratchPlatesForNotePartVariables>(
    GET_SCRATCH_PLATES_FOR_NOTE_PART_QUERY,
    {
      variables: {ids: scratchPlates.map((scratchPlate) => scratchPlate.scratchPlateId)},
      skip: scratchPlates.length === 0,
    }
  );
  const recordsItems =
    query.type === QueryResultType.SUCCESS ? buildScratchPlateRecordsItems(query.data.scratchPlates) : [];

  const firstPlateId = recordsItems.find((item) => item.kind === 'plate')?.id ?? null;
  const [selectedPlateId, setSelectedPlateId] = useState<string | null>(null);
  const selectedOrDefaultPlateId =
    selectedPlateId != null &&
    recordsItems.some((item) => item.kind === 'plate' && item.id === selectedPlateId)
      ? selectedPlateId
      : firstPlateId;
  const selectedDisplayPlate =
    selectedOrDefaultPlateId != null
      ? (recordsItems.find(
          (item): item is Extract<RecordsListItem, {kind: 'plate'}> =>
            item.kind === 'plate' && item.id === selectedOrDefaultPlateId
        ) ?? null)
      : null;
  const selectedQueryPlate =
    query.type === QueryResultType.SUCCESS && selectedOrDefaultPlateId != null
      ? (query.data.scratchPlates.find(
          (item): item is ScratchPlateNode => !isInaccessible(item) && item.id === selectedOrDefaultPlateId
        ) ?? null)
      : null;
  const designPlateSize: PlateSize = [selectedQueryPlate?.height ?? 8, selectedQueryPlate?.width ?? 12];
  const designPlateSchemaId = selectedQueryPlate?.plateSchemaId ?? null;
  const subheader =
    scratchPlates.length === 1
      ? (selectedDisplayPlate?.name ??
        scratchPlates[0]?.scratchPlateName ??
        scratchPlates[0]?.scratchPlateId ??
        '')
      : `${scratchPlates.length} plates`;

  const queryResultBody =
    query.type === QueryResultType.PENDING ? (
      <CenteredHorizontalThrobber />
    ) : query.type === QueryResultType.ERROR ? (
      <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load scratch plates." />
    ) : (
      <ScratchPlateRecordsView
        recordsItems={recordsItems}
        selectedPlateId={selectedOrDefaultPlateId}
        onSelectPlateId={setSelectedPlateId}
        designPlateSize={designPlateSize}
        designPlateSchemaId={designPlateSchemaId}
      />
    );

  return (
    <PlateMapContainer
      clientStateId={`note-linked-scratch-plates-${notePartCid}`}
      header="Scratch plates"
      headerControls={null}
      onHeaderContextMenu={noop}
      subheader={subheader}
      plateMapType={PlateMapType.RECORD}
    >
      {queryResultBody}
    </PlateMapContainer>
  );
}
